import { Icon } from "@atoms";
import { APIDeckTaxStrategies } from "@constants/choices";
import { PERMISSION_LEVEL } from "@constants/permissionLevel";
import ClientSearchOrCreateSelect, { clientToClientOption } from "@legacy/clients/inputs/ClientSearchOrCreateSelect";
import { renderClientString } from "@legacy/clients/utils/utils";
import Spinner from "@legacy/core/components/Spinner";
import TagField from "@legacy/core/fields/TagField";
import TagSearchInput from "@legacy/core/inputs/TagSearchInput";
import { valueIsDefined } from "@legacy/core/utils/utils";
import InvoiceObjectLink from "@legacy/invoices/components/InvoiceObjectLink";
import { FileUpload } from "@molecules";
import { AccessCheck } from "@particles/index";
import { faIcon } from "@utils/faIcon";
import { Component, Fragment } from "react";
import ButtonGroup from "../../core/buttons/ButtonGroup";
import ButtonGroupRow from "../../core/buttons/ButtonGroupRow";
import UniversalButton from "../../core/buttons/UniversalButton";
import Banner from "../../core/components/Banner";
import LineItemListSelectFieldGroup from "../../core/components/LineItemListSelectFieldGroup";
import BasicDisplayField from "../../core/fields/BasicDisplayField";
import CharField from "../../core/fields/CharField";
import DateField from "../../core/fields/DateField";
import LinkedObjectsDisplayField from "../../core/fields/LinkedObjectsDisplayField";
import SearchCreateSelectField from "../../core/fields/SearchCreateSelectField";
import TextField from "../../core/fields/TextField";
import { calculateInvoiceAmounts } from "../../invoices/utils/utils";
import PriceBookTaxSearchOrCreateSelect, { getPriceBookTaxObjectLabel, priceBookTaxToPriceBookTaxOption } from "../../pricebook/inputs/PriceBookTaxSearchOrCreateSelect";
import CreditNoteFeedAccordion from "../components/CreditNoteFeedAccordion";

class CreditNoteForm extends Component {

    constructor(props) {
        super(props)

        this.state = {
            clientSearchInput: "",
            taxSearchInput: "",

            triggerCommit: false,
            triggerDiscard: false,
            pendingAction: null,
        }
    }

    handleSaveAction = (action) => {
        const { creditNote, requestAction } = this.props

        if (creditNote.id && window.FILE_UPLOADS_ENABLED) {
            this.setState({ triggerCommit: true, pendingAction: action })
        } else {
            requestAction(action)
        }
    }

    handleCancelAction = (action) => {
        const { creditNote, requestAction } = this.props

        if (creditNote.id && window.FILE_UPLOADS_ENABLED) {
            this.setState({ triggerDiscard: true, pendingAction: action })
        } else {
            requestAction(action)
        }
    }

    componentDidMount() {
        // Return to the previous scroll
        document.querySelector(".main").scrollTo(0, this.props.returnScroll || 0)
    }

    renderButtons = () => {
        const {
            mode,
            submitting,
            creditNote,
            errors,
            requestAction,
            isUploading,
        } = this.props

        if (errors.unexpectedError) {
            return (
                <div className="data-panel__action-feedback">
                    <span className="text-invalid"><strong>An unexpected error occurred.</strong></span>
                </div>
            )
        }
        else {
            if (submitting) {
                return (
                    <div className="data-panel__action-feedback">
                        <Spinner centered={true} />
                    </div>
                )
            }
            else {
                if (mode === "ADD_CREDIT_NOTE") {
                    const showDraftButtonAtTheTop = window.CURRENT_USER?.permissions.invoices_edit_permission !== PERMISSION_LEVEL.FULL
                        && window.CURRENT_USER?.permissions.invoices_create_permission === PERMISSION_LEVEL.RESTRICTED

                    return (
                        <ButtonGroup>
                            {showDraftButtonAtTheTop && (
                                <ButtonGroupRow>
                                    <UniversalButton type="primary" text="Save Draft" handler={event => this.handleSaveAction("CREDIT_NOTE_CREATE_DRAFT")} disabled={isUploading} />
                                </ButtonGroupRow>
                            )}
                            <AccessCheck permissions={{
                                invoices_create_permission: PERMISSION_LEVEL.FULL
                            }}>
                                <ButtonGroupRow>
                                    <UniversalButton type="primary" text={window.CURRENT_USER?.permissions.invoices_edit_permission === PERMISSION_LEVEL.FULL ? "Create and Send" : "Create"} handler={event => this.handleSaveAction("CREDIT_NOTE_CREATE")} disabled={isUploading} />
                                </ButtonGroupRow>
                            </AccessCheck>
                            <ButtonGroupRow>
                                <AccessCheck permissions={{
                                    invoices_view_permission: PERMISSION_LEVEL.FULL
                                }}>
                                    <UniversalButton type="secondary" text="Preview Credit Note" handler={event => this.handleSaveAction("CREDIT_NOTE_PREVIEW")} disabled={isUploading} />
                                </AccessCheck>
                                {
                                    !showDraftButtonAtTheTop && (
                                        <AccessCheck permissions={{
                                            invoices_create_permission: PERMISSION_LEVEL.RESTRICTED
                                        }}>
                                            <UniversalButton type="secondary" text="Save Draft" handler={event => this.handleSaveAction("CREDIT_NOTE_CREATE_DRAFT")} disabled={isUploading} />
                                        </AccessCheck>
                                    )
                                }
                            </ButtonGroupRow>
                        </ButtonGroup>
                    )
                }
                else if (mode === "EDIT_CREDIT_NOTE") {
                    // Only drafts can be edited
                    return (
                        <ButtonGroup>
                            <AccessCheck permissions={{
                                invoices_create_permission: PERMISSION_LEVEL.FULL
                            }}>
                                <ButtonGroupRow>
                                    <UniversalButton type="primary" text={window.CURRENT_USER?.permissions.invoices_edit_permission === PERMISSION_LEVEL.FULL ? "Create and Send" : "Create"} handler={event => this.handleSaveAction("CREDIT_NOTE_CREATE")} disabled={isUploading} />
                                </ButtonGroupRow>
                            </AccessCheck>
                            <ButtonGroupRow>
                                <UniversalButton type="secondary" text="Save Draft" handler={event => this.handleSaveAction("CREDIT_NOTE_CREATE_DRAFT")} disabled={isUploading} />
                                <AccessCheck permissions={{
                                    invoices_delete_permission: PERMISSION_LEVEL.RESTRICTED
                                }}>
                                    <UniversalButton type="danger" text="Delete Draft" handler={event => this.handleCancelAction("CREDIT_NOTE_DELETE_DRAFT")} />
                                </AccessCheck>
                            </ButtonGroupRow>
                        </ButtonGroup>
                    )
                }
            }
        }
    }

    render() {
        const {
            mode,
            submitting,
            creditNote,
            invoice,
            errors,
            onFormDataChange,
            requestAction,
            switchToSecondaryForm,
            updateClientSelection,
            updatePriceBookTaxSelection,
            selectedClient,
            selectedPriceBookTax,
            formatCurrencyValue,
            showCustomCreditNoteIDField,
            showQuickBooksItemSelect=false,
            showTaxCreateButton=false,
            useTaxes=false,
            hideTaxRates=false,
            updateAttachments,
            removePendingAttachment,
            pendingSessionId,
            isUploading,
            setIsUploading,
            returnScroll,
        } = this.props

        const {subtotal, tax, total} = calculateInvoiceAmounts(creditNote)

        const allowClientDetailsEdit = Object.keys(invoice).length === 0 && (creditNote.invoice === null || creditNote.invoice === undefined)

        return (
            <Fragment>
                <div id="credit_note_form_react_wrapper">
                    <div className="data-panel-container data-panel-container--with-margin">
                        <div className="data-panel" aria-label="Credit Note Create/Update">
                            <div className="data-panel__form" aria-label="Credit Note Create/Update Form">
                                <p className="data-panel__form__caption">Please provide the following information to create your credit note:</p>
                                <fieldset>
                                    <legend>Client Details</legend>
                                    <SearchCreateSelectField
                                        fieldName="external_client"
                                        fieldLabel="Client"
                                        fieldValue={selectedClient !== null ? renderClientString(selectedClient) : null}
                                        inputComponent={
                                            <ClientSearchOrCreateSelect
                                                disabled={!allowClientDetailsEdit || window.CURRENT_USER?.permissions.clients_list_permission < PERMISSION_LEVEL.RESTRICTED}
                                                onSelectionChange={selectedOption => updateClientSelection(selectedOption != null ? selectedOption.object : null)}
                                                onInputChange={(input, action) => {action.action === "input-change" && this.setState({clientSearchInput: input})}}
                                                defaultSelected={selectedClient !== null ? clientToClientOption(selectedClient) : null}
                                                showCreateButton={window.CURRENT_USER?.permissions.clients_create_permission >= PERMISSION_LEVEL.FULL}
                                                onCreateClick={event => switchToSecondaryForm("ADD_CLIENT", null, {name: this.state.clientSearchInput})}
                                                listEndpointKwargs={[
                                                    ["is_active", true]
                                                ]}
                                            ></ClientSearchOrCreateSelect>
                                        }
                                        showButton={allowClientDetailsEdit && selectedClient !== null && window.CURRENT_USER?.permissions.clients_edit_permission >= PERMISSION_LEVEL.FULL}
                                        buttonLabel={<Fragment><Icon containerClassName="icon-inline" icon={faIcon({ name: "pen-to-square", style: "regular" })} />Edit Client</Fragment>}
                                        buttonAction={event => switchToSecondaryForm("EDIT_CLIENT", selectedClient, null)}
                                        disabled={!allowClientDetailsEdit}
                                        errors={errors}
                                    ></SearchCreateSelectField>
                                    {
                                        selectedClient !== null && selectedClient.notes !== "" && (
                                            <BasicDisplayField
                                                fieldName="client_notes"
                                                fieldLabel="Client Notes"
                                                fieldValue={selectedClient.notes}
                                                indented={true}
                                            ></BasicDisplayField>
                                        )
                                    }
                                </fieldset>
                                {
                                    selectedClient !== null && (
                                        <fieldset>
                                            <legend>Basic Details</legend>
                                            {
                                                Object.keys(invoice).length !== 0 && !(creditNote.invoice === null || creditNote.invoice === undefined) && (
                                                    <AccessCheck entitlements={["entitlement_invoices_enabled"]}>
                                                        <LinkedObjectsDisplayField
                                                            fieldName="invoice"
                                                            fieldLabel="Created From Invoice"
                                                            objectComponentList={[
                                                                <InvoiceObjectLink
                                                                    key="parent_invoice_0"
                                                                    invoice={invoice}
                                                                    destination={DjangoUrls["invoices:invoices-detail"](window.MARKETPLACE_ENTITY_SLUG, invoice.id)}
                                                                    disabled={window.CURRENT_USER?.permissions.invoices_view_permission < PERMISSION_LEVEL.FULL}
                                                                />
                                                            ]}
                                                        ></LinkedObjectsDisplayField>
                                                    </AccessCheck>
                                                )
                                            }
                                            {
                                                showCustomCreditNoteIDField
                                                ?
                                                <CharField
                                                    fieldName="custom_id"
                                                    fieldLabel="Credit Note ID"
                                                    fieldValue={creditNote.custom_id || ""}
                                                    fieldOnChange={custom_id => onFormDataChange("custom_id", custom_id || "")}
                                                    maxLength="15"
                                                    errors={errors}
                                                ></CharField>
                                                :
                                                <div id="div_id_custom_id">{errors.custom_id && <Banner type="warning" text={errors.custom_id} extraMargin={true} />}</div>
                                            }
                                            <DateField
                                                fieldName="date_issued"
                                                fieldLabel="Issue Date"
                                                fieldValue={creditNote.date_issued || ""}
                                                fieldOnChange={date_issued => onFormDataChange("date_issued", date_issued || "")}
                                                errors={errors}
                                            ></DateField>
                                            <TextField
                                                fieldName="reason"
                                                fieldLabel="Reason"
                                                fieldValue={creditNote.reason || ""}
                                                fieldOnChange={reason => onFormDataChange("reason", reason || "")}
                                                rows={3}
                                                errors={errors}
                                            ></TextField>
                                            <TagField
                                                fieldName="labels"
                                                fieldLabel="Labels"
                                                inputComponent={
                                                    <TagSearchInput
                                                        defaultSelectedTags={creditNote.labels?.map(label => label.name) ?? []}
                                                        onSelectionChange={labelNames => onFormDataChange("labels", labelNames ? labelNames.map(labelName => ({"name": labelName})) : [])}
                                                        choicesEndpoint={DjangoUrls["invoices:api-credit-notes-labels-list"](window.MARKETPLACE_ENTITY_SLUG)}
                                                        placeholder="Add Labels..."
                                                        noOptionsMessage="No Labels Found"
                                                        isMulti={true}
                                                    ></TagSearchInput>
                                                }
                                                optional={true}
                                                errors={errors}
                                            ></TagField>
                                        </fieldset>
                                    )
                                }
                                {
                                    selectedClient !== null && (
                                        <fieldset>
                                            <legend>Charge Details</legend>
                                            <br />
                                            <AccessCheck permissions={{
                                                pricebook_list_permission: PERMISSION_LEVEL.RESTRICTED,
                                            }}>
                                                <AccessCheck permissions={{
                                                    pricebook_view_permission: PERMISSION_LEVEL.FULL,
                                                }}>
                                                    <LineItemListSelectFieldGroup
                                                        object={creditNote}
                                                        formatCurrencyValue={formatCurrencyValue}
                                                        showQuickBooksItemSelect={showQuickBooksItemSelect}
                                                        useTaxes={useTaxes}
                                                        switchToSecondaryForm={switchToSecondaryForm}
                                                        errors={errors}
                                                        nonFieldErrorMessage={errors.line_items || "There's an issue with one or more of the items below. Try coming back to this page and trying again in a bit."}
                                                        isCreditNoteLineItem={true}
                                                        parentLineItems={invoice?.line_items || []}
                                                        parentLineItemFieldName="invoice_line_item"
                                                        parentLineItemLabel="Invoice Line Item"
                                                    ></LineItemListSelectFieldGroup>
                                                </AccessCheck>
                                            </AccessCheck>
                                            {
                                                useTaxes && (
                                                    Object.keys(invoice).length !== 0 && !(creditNote.invoice === null || creditNote.invoice === undefined)
                                                    ?
                                                    (
                                                        <BasicDisplayField
                                                            fieldName="tax_name"
                                                            fieldLabel="Tax Rate"
                                                            fieldValue={creditNote.tax_name ? getPriceBookTaxObjectLabel({ description: creditNote.tax_name, default_price: creditNote.tax_percent }) : "No Tax"}
                                                        ></BasicDisplayField>
                                                    )
                                                    :
                                                    (
                                                        !hideTaxRates ? (
                                                            <SearchCreateSelectField
                                                                key={creditNote.tax_name}
                                                                fieldName="tax_name"
                                                                fieldLabel="Tax Rate"
                                                                fieldValue={selectedPriceBookTax ? selectedPriceBookTax.description : (creditNote.tax_name || null)}
                                                                inputComponent={
                                                                    <PriceBookTaxSearchOrCreateSelect
                                                                        disabled={window.CURRENT_USER?.permissions.pricebook_list_permission < PERMISSION_LEVEL.RESTRICTED}
                                                                        onSelectionChange={selectedOption => updatePriceBookTaxSelection(selectedOption != null ? selectedOption.object : null)}
                                                                        onInputChange={(input, action) => {action.action === "input-change" && this.setState({taxSearchInput: input})}}
                                                                        defaultSelected={
                                                                            selectedPriceBookTax
                                                                            ?
                                                                            priceBookTaxToPriceBookTaxOption(selectedPriceBookTax)
                                                                            :
                                                                            (creditNote.tax_name ? { value: null, label: getPriceBookTaxObjectLabel({ description: creditNote.tax_name, default_price: creditNote.tax_percent })} : null)
                                                                        }
                                                                        showCreateButton={showTaxCreateButton && window.CURRENT_USER?.permissions.pricebook_create_permission >= PERMISSION_LEVEL.FULL}
                                                                        limitToQuickBooksSyncedItems={!showTaxCreateButton}
                                                                        onCreateClick={event => switchToSecondaryForm("ADD_PRICEBOOKITEM_TAX", null, {description: this.state.taxSearchInput})}
                                                                    />
                                                                }
                                                                showButton={selectedPriceBookTax !== null && window.CURRENT_USER?.permissions.pricebook_create_permission >= PERMISSION_LEVEL.FULL}
                                                                buttonLabel={<Fragment><Icon containerClassName="icon-inline" icon={faIcon({ name: "pen-to-square", style: "regular" })} />Edit Tax Rate</Fragment>}
                                                                buttonAction={event => switchToSecondaryForm("EDIT_PRICEBOOKITEM_TAX", selectedPriceBookTax, null)}
                                                                errors={errors}
                                                            />
                                                        )
                                                        :
                                                        (
                                                            window.CURRENT_USER.service_company.apideck_tax_strategy === APIDeckTaxStrategies.automatic
                                                            ?
                                                            <>
                                                                <BasicDisplayField
                                                                    fieldName="tax_name"
                                                                    fieldLabel="Tax Rate"
                                                                    fieldValue={creditNote.tax_name ? getPriceBookTaxObjectLabel({ description: creditNote.tax_name, default_price: creditNote.tax_percent }) : ""}
                                                                />
                                                                <Banner type="info" text="The Tax Rate will be automatically calculated once created and synced to your accounting integration. If a 0% rate comes back, make sure tax is properly configured for the client in your accounting integration and then re-save this credit note." extraMargin={true} />
                                                            </>
                                                            :
                                                            <></>
                                                        )
                                                    )
                                                )
                                            }
                                            {
                                                window.FILE_UPLOADS_ENABLED && creditNote.id && (
                                                    <div className="data-panel__form__field">
                                                        <FileUpload
                                                            mode="attached"
                                                            objectType="credit_note"
                                                            objectId={creditNote.id}
                                                            existingAttachments={creditNote.attachments}
                                                            autoUpload={true}
                                                            autoCommit={false}
                                                            triggerCommit={this.state.triggerCommit}
                                                            triggerDiscard={this.state.triggerDiscard}
                                                            label={{ label: "Attachments", condition: "optional" }}
                                                            onCommitComplete={() => {
                                                                const action = this.state.pendingAction
                                                                this.setState({ triggerCommit: false, pendingAction: null })
                                                                if (action) {
                                                                    requestAction(action)
                                                                }
                                                            }}
                                                            onDiscardComplete={() => {
                                                                const action = this.state.pendingAction
                                                                this.setState({ triggerDiscard: false, pendingAction: null })
                                                                if (action) {
                                                                    requestAction(action)
                                                                }
                                                            }}
                                                            onUploadingChange={(uploading) => {
                                                                if (setIsUploading) {
                                                                    setIsUploading(uploading)
                                                                }
                                                            }}
                                                            onFileUploadError={(file, error) => {
                                                                console.error("Credit note attachment upload error:", file.name, error)
                                                            }}
                                                        />
                                                    </div>
                                                )
                                            }
                                            {
                                                window.FILE_UPLOADS_ENABLED && !creditNote.id && (
                                                    <div className="data-panel__form__field">
                                                        <FileUpload
                                                            mode="pending"
                                                            objectType="credit_note"
                                                            sessionId={pendingSessionId}
                                                            autoUpload={true}
                                                            label={{ label: "Attachments", condition: "optional" }}
                                                            onPendingFileUploadComplete={(result) => {
                                                                updateAttachments([{
                                                                    file: result.file,
                                                                    filePath: result.filePath,
                                                                    fileUrl: result.fileUrl,
                                                                    upload_filename: result.file.name,
                                                                    upload_mimetype: result.file.type,
                                                                    upload_size: result.file.size,
                                                                    sessionId: result.sessionId,
                                                                    isPending: true,
                                                                }])
                                                            }}
                                                            onFileRemove={(filename) => {
                                                                if (removePendingAttachment) {
                                                                    removePendingAttachment(filename)
                                                                }
                                                            }}
                                                            onUploadingChange={(uploading) => {
                                                                if (setIsUploading) {
                                                                    setIsUploading(uploading)
                                                                }
                                                            }}
                                                            onFileUploadError={(file, error) => {
                                                                console.error("Pending credit note attachment upload error:", file.name, error)
                                                            }}
                                                        />
                                                    </div>
                                                )
                                            }
                                        </fieldset>
                                    )
                                }
                            </div>
                            {
                                selectedClient !== null && (
                                    <div className="data-panel__form data-panel__data-summary amount-summary" aria-label="Amount Summary">
                                        <hr aria-hidden="true" />
                                        <div className="data-panel__data-summary__row amount-summary__item amount-summary__item--subtotal" aria-label="Subtotal">
                                            <div className="data-panel__data-summary__data__label"><span>Subtotal</span></div>
                                            <div className="data-panel__data-summary__data__value">{formatCurrencyValue(subtotal)}</div>
                                        </div>
                                        {
                                            useTaxes && (
                                                <div className="data-panel__data-summary__row amount-summary__item amount-summary__item--tax" aria-label="Tax">
                                                    {
                                                        hideTaxRates
                                                        ?
                                                        <div className="data-panel__data-summary__data__label"><span title={creditNote.tax_breakdown?.map(component => `[${component.rate}%] ${component.name}`).join("\n")}>Tax ({creditNote.tax_percent ? `${creditNote.tax_percent}%` : "none"})</span></div>
                                                        :
                                                        <div className="data-panel__data-summary__data__label"><span>Tax ({selectedPriceBookTax ? `${selectedPriceBookTax.default_price}%` : "none"})</span></div>
                                                    }
                                                    <div className="data-panel__data-summary__data__value">{formatCurrencyValue(tax)}</div>
                                                </div>
                                            )
                                        }
                                        <hr aria-hidden="true" />
                                        <div className="data-panel__data-summary__row amount-summary__item amount-summary__item--total" aria-label="Total">
                                            <div className="data-panel__data-summary__data__label"><span>Total</span></div>
                                            <div className="data-panel__data-summary__data__value">{formatCurrencyValue(total)}</div>
                                        </div>
                                    </div>
                                )
                            }
                            {
                                selectedClient !== null && this.renderButtons()
                            }
                        </div>
                    </div>
                </div>
                <AccessCheck permissions={{
                    invoices_view_permission: PERMISSION_LEVEL.FULL
                }}>
                    {
                        valueIsDefined(creditNote.id) && (
                            <div className="accordion-wrapper">
                                <CreditNoteFeedAccordion creditNoteID={creditNote.id} />
                            </div>
                        )
                    }
                </AccessCheck>
            </Fragment>
        )
    }

}

export default CreditNoteForm;
